import type EntityMember from "_shared/entity-members/entity-member";
import type { TeamPeopleChange } from "work/work-agreements/teams/people-picker/team-people-selection";

/** A roster as its owner holds it: one leader, or none yet, and the members. */
export type TeamRosterValue = {
  leader: EntityMember | null;
  members: EntityMember[];
};

/**
 * The roster once the people picker's change is applied (#6602). One rule for every roster that
 * takes the picker, so a template and a draft proposal cannot disagree about it:
 *
 * - The chosen leader leads. Whoever led before stays on the team as a member only when the change
 *   says so (`formerLeaderStays`), and otherwise leaves it. A leader is exempt from the credentials a
 *   member needs (#5800), so keeping them is a choice the picker's tray asks for, not a default.
 * - A person is on the roster once. Somebody already on it, or chosen twice, is not added again, and
 *   the leader is never also a member — `VendorTeam.ValidateInputAsync` refuses that roster outright.
 */
export function applyTeamPeopleChange(roster: TeamRosterValue, change: TeamPeopleChange): TeamRosterValue {
  const leader = change.leader ?? roster.leader;
  const replacesLeader = Boolean(change.leader && roster.leader && !roster.leader.isSamePersonAs(change.leader));
  const formerLeader = replacesLeader && change.formerLeaderStays ? roster.leader : null;
  const members: EntityMember[] = [];
  for (const candidate of [...roster.members, ...(formerLeader ? [formerLeader] : []), ...change.members]) {
    if (leader?.isSamePersonAs(candidate)) continue;
    if (members.some((member) => member.isSamePersonAs(candidate))) continue;
    members.push(candidate);
  }
  return { leader, members };
}
